import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PencilIcon,
  XCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Spinner,
  ConfirmDialog,
  Modal,
  Input,
  Avatar,
} from '../components/ui';
import { useOrderStore, useAuthStore } from '../stores';
import type { OrderTransaction, OrderShipment, OrderStatusUpdate, UpdateShippingData, ShippingProvider, ShipmentStatus } from '../types';
import { formatDate, formatDateTime, formatCurrency, getStatusDisplayName, getStatusBadgeVariant, isActionRequired } from '../utils';

/**
 * Get the tracking URL for a shipping provider
 */
const getTrackingUrl = (provider: ShippingProvider | string, trackingNumber: string): string => {
  const urls: Record<string, string> = {
    ups: `https://www.ups.com/track?tracknum=${trackingNumber}`,
    usps: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
    fedex: `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
  };
  return urls[provider?.toLowerCase()] || '#';
};

/**
 * Format shipping provider name for display
 */
const formatProviderName = (provider: ShippingProvider | string): string => {
  const names: Record<string, string> = {
    ups: 'UPS',
    usps: 'USPS',
    fedex: 'FedEx',
  };
  return names[provider?.toLowerCase()] || provider?.toUpperCase() || 'Unknown';
};

/**
 * Get shipment status badge variant
 */
const getShipmentStatusVariant = (status: ShipmentStatus | string | null | undefined): 'success' | 'warning' | 'primary' | 'accent' => {
  const variants: Record<string, 'success' | 'warning' | 'primary' | 'accent'> = {
    delivered: 'success',
    out_for_delivery: 'accent',
    in_transit: 'primary',
    pending: 'warning',
  };
  return variants[status || 'pending'] || 'warning';
};

/**
 * Format shipment status for display
 */
const formatShipmentStatus = (status: ShipmentStatus | string | null | undefined): string => {
  const labels: Record<string, string> = {
    delivered: 'Delivered',
    out_for_delivery: 'Out for Delivery',
    in_transit: 'In Transit',
    pending: 'Pending',
  };
  return labels[status || 'pending'] || 'Pending';
};

// Transaction-specific status variant (different from order status)
const getTransactionStatusVariant = (status: string) => {
  const normalizedStatus = status?.toLowerCase();
  const variants: Record<string, 'success' | 'warning' | 'primary' | 'accent' | 'danger'> = {
    settled: 'success',
    pending: 'warning',
    refunded: 'danger',
    voided: 'danger',
  };
  return variants[normalizedStatus] || 'primary';
};

const getStatusIcon = (status: string) => {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'delivered' || statusLower === 'settled') {
    return <CheckCircleIcon className="h-4 w-4" />;
  }
  if (statusLower === 'cancelled' || statusLower === 'voided' || statusLower === 'refunded') {
    return <XMarkIcon className="h-4 w-4" />;
  }
  return <ClockIcon className="h-4 w-4" />;
};

/**
 * Format raw webhook event names for display in Activity timeline
 * e.g., "CASE_CREATED" → "Case Created", "PAYMENT_CAPTURED" → "Payment Captured"
 */
const formatRawStatus = (status: string) => {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get badge variant for raw webhook event statuses
 * Maps raw statuses to colors based on their meaning
 */
const getRawStatusVariant = (status: string): 'success' | 'warning' | 'primary' | 'accent' | 'danger' => {
  const upperStatus = status?.toUpperCase();
  
  // Success states (green)
  if (['DELIVERED', 'COMPLETED'].includes(upperStatus)) {
    return 'success';
  }
  
  // Danger/Cancelled states (red)
  if (['CASE_REJECTED', 'PRESCRIPTION_REJECTED', 'REFUNDED', 'CANCELLED', 
       'SUBSCRIPTION_CANCELLED', 'CASE_CANCELLED', 'SUBSCRIPTION_TERMINATED',
       'PAYMENT_FAILED', 'PAYMENT_CAPTURE_FAILED', 'HOLD_RELEASE_FAILED',
       'SUBSCRIPTION_CREATION_FAILED', 'SUBSCRIPTION_CHARGE_FAILED'].includes(upperStatus)) {
    return 'danger';
  }
  
  // Warning/Action required states (yellow/amber)
  if (['DRIVERS_LICENSE_REQUESTED', 'INTRO_VIDEO_REQUESTED', 'FILE_UPLOAD_REQUESTED',
       'EXAM_REQUESTED', 'CASE_TRANSFERRED_TO_SUPPORT'].includes(upperStatus)) {
    return 'warning';
  }
  
  // Shipping states (purple/accent)
  if (['SHIPPED', 'SHIPPING_OUT_FOR_DELIVERY', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(upperStatus)) {
    return 'accent';
  }
  
  // Default: primary (blue) for processing/pending states
  return 'primary';
};

export const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const {
    selectedOrder,
    isLoading,
    getOrderById,
    cancelOrder,
    refundTransaction,
    updateShipping,
  } = useOrderStore();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<OrderTransaction | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);

  const [shippingForm, setShippingForm] = useState<UpdateShippingData>({
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
  });

  useEffect(() => {
    if (id) {
      getOrderById(id);
    }
  }, [id, getOrderById]);

  useEffect(() => {
    if (selectedOrder?.shipping) {
      setShippingForm({
        streetAddress1: selectedOrder.shipping.streetAddress1 || '',
        streetAddress2: selectedOrder.shipping.streetAddress2 || '',
        city: selectedOrder.shipping.city || '',
        state: selectedOrder.shipping.state || '',
        postalCode: selectedOrder.shipping.postalCode || '',
      });
    }
  }, [selectedOrder?.shipping]);

  const handleCancelOrder = async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      await cancelOrder(id);
      toast.success('Order cancelled successfully');
      navigate('/orders');
    } catch {
      toast.error('Failed to cancel order');
    } finally {
      setIsCancelling(false);
      setCancelDialogOpen(false);
    }
  };

  const handleRefundTransaction = async () => {
    if (!id || !selectedTransaction) return;
    setIsRefunding(true);
    try {
      await refundTransaction(id, selectedTransaction.id);
      toast.success('Transaction refunded successfully');
    } catch {
      toast.error('Failed to refund transaction');
    } finally {
      setIsRefunding(false);
      setRefundDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  const handleSaveShipping = async () => {
    if (!id) return;
    setIsSavingShipping(true);
    try {
      await updateShipping(id, shippingForm);
      toast.success('Shipping address updated');
      setShippingModalOpen(false);
    } catch {
      toast.error('Failed to update shipping address');
    } finally {
      setIsSavingShipping(false);
    }
  };

  const openRefundDialog = (transaction: OrderTransaction) => {
    setSelectedTransaction(transaction);
    setRefundDialogOpen(true);
  };

  if (isLoading && !selectedOrder) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!selectedOrder) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500 dark:text-secondary-400">Order not found</p>
        <Link to="/orders" className="text-primary-500 hover:underline mt-2 inline-block">
          Back to Orders
        </Link>
      </div>
    );
  }

  const order = selectedOrder;
  const canCancel = hasPermission('orders:cancel') && !order.cancelled && !order.refunded;
  const canRefund = hasPermission('orders:refund');
  const canUpdateShipping = hasPermission('orders:update');
  const totalAmount = (order.chargeAmount || 0) + (order.taxedAmount || 0) + (order.shippingCharge || 0);
  const currentStatus = order.recentStatus || order.status || 'pending';
  
  // Check if order requires action and get the reason
  const standardizedStatus = order.standardizedStatus;
  const actionReason = order.actionReason;
  const statusToDisplay = standardizedStatus || currentStatus;
  const requiresAction = isActionRequired(statusToDisplay);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to="/orders" className="inline-flex items-center text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 mb-3 text-sm">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Orders
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
              {order.orderCode || `#${order.id.slice(0, 8)}`}
            </h1>
            <Badge variant={getStatusBadgeVariant(statusToDisplay)} dot>
              {getStatusDisplayName(statusToDisplay)}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {canUpdateShipping && (
            <Button variant="secondary" size="sm" leftIcon={<PencilIcon className="h-4 w-4" />} onClick={() => setShippingModalOpen(true)}>
              Edit Shipping
            </Button>
          )}
          {canCancel && (
            <Button variant="danger" size="sm" leftIcon={<XCircleIcon className="h-4 w-4" />} onClick={() => setCancelDialogOpen(true)}>
              Cancel Order
            </Button>
          )}
        </div>
      </div>

      {/* Action Required Alert */}
      {requiresAction && (
        <div className="bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-danger-100 dark:bg-danger-800/50 rounded-lg shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-danger-600 dark:text-danger-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-danger-800 dark:text-danger-200">Action Required</h3>
              <p className="text-sm text-danger-700 dark:text-danger-300 mt-1">
                {actionReason || 'This order requires attention before it can proceed. Please review the order status for more details.'}
              </p>
              {order.accessLink && (
                <a
                  href={order.accessLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-danger-700 dark:text-danger-300 hover:text-danger-800 dark:hover:text-danger-200 mt-2"
                >
                  Patient Action Link
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="sm" className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Total</p>
              <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-50 dark:bg-accent-900/30 rounded-lg">
              <CalendarDaysIcon className="h-5 w-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Order Date</p>
              <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">{formatDate(order.createdAt)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-50 dark:bg-success-900/30 rounded-lg">
              <TruckIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Shipping</p>
              <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">{order.shippingCharge ? formatCurrency(order.shippingCharge) : 'Free'}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm" className="!p-4">
          <div className="flex items-center gap-3">
            {order.subscriptionIntervalMonths ? (
              <>
                <div className="p-2 bg-warning-50 dark:bg-warning-900/30 rounded-lg">
                  <ArrowPathIcon className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Subscription</p>
                  <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Every {order.subscriptionIntervalMonths}mo</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Order Type</p>
                  <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">One-time</p>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardHeader title="Order Items" />
            <div className="p-6 pt-0">
              {(() => {
                // Use orderItems if available (V2 multi-item), fall back to single offering (V1)
                const items = order.orderItems && order.orderItems.length > 0
                  ? order.orderItems.map(item => item.offering).filter(Boolean)
                  : order.offering ? [order.offering] : [];

                if (items.length === 0) {
                  return <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">No items found</p>;
                }

                return (
                  <div className="space-y-3">
                    {items.map((offering, index) => (
                      <div key={offering!.id || index} className="flex items-center gap-4 p-4 bg-secondary-50 dark:bg-secondary-800 rounded-xl">
                        {offering!.offeringImageUrl ? (
                          <img
                            src={offering!.offeringImageUrl}
                            alt={offering!.offeringName}
                            className="w-16 h-16 rounded-lg object-cover border border-secondary-200 dark:border-secondary-700"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-secondary-200 dark:bg-secondary-700 flex items-center justify-center">
                            <CurrencyDollarIcon className="h-6 w-6 text-secondary-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-secondary-900 dark:text-secondary-100">{offering!.offeringName}</p>
                          {offering!.offeringDescription && (
                            <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">{offering!.offeringDescription}</p>
                          )}
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">Qty: {offering!.quantity || 1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </Card>

          {/* Transactions */}
          <Card>
            <CardHeader title="Payment History" />
            <div className="p-6 pt-0">
              {order.transactions && order.transactions.length > 0 ? (
                <div className="space-y-3">
                  {order.transactions.map((tx: OrderTransaction) => (
                    <div
                      key={tx.id}
                      className="p-4 bg-secondary-50 dark:bg-secondary-800 rounded-xl space-y-3"
                    >
                      {/* Payment Info Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            tx.status === 'settled' ? 'bg-success-100' :
                            tx.status === 'voided' || tx.status === 'refunded' ? 'bg-danger-100' :
                            'bg-warning-100'
                          }`}>
                            {getStatusIcon(tx.status)}
                          </div>
                          <div>
                            <p className="font-medium text-secondary-900 dark:text-secondary-100">{formatCurrency(tx.amount)}</p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 font-mono">{tx.transactionId}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <Badge variant={getTransactionStatusVariant(tx.status)} size="sm">
                              {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                            </Badge>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">{formatDate(tx.createdAt)}</p>
                          </div>
                          {canRefund && tx.status === 'settled' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openRefundDialog(tx)}
                              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                            >
                              Refund
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Shipping Tracking Row */}
                      {tx.trackingNumber && tx.shippingProvider && (
                        <div className="flex items-center justify-between pt-3 border-t border-secondary-200 dark:border-secondary-700">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-accent-50 dark:bg-accent-900/30 rounded-lg">
                              <TruckIcon className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                                {formatProviderName(tx.shippingProvider)}
                              </p>
                              <p className="text-xs text-secondary-500 dark:text-secondary-400 font-mono">{tx.trackingNumber}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={getShipmentStatusVariant(tx.shipmentStatus)} size="sm">
                              {formatShipmentStatus(tx.shipmentStatus)}
                            </Badge>
                            <a
                              href={getTrackingUrl(tx.shippingProvider, tx.trackingNumber)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                            >
                              Track
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">No transactions found</p>
              )}
            </div>
          </Card>

          {/* Shipments (supports multiple tracking numbers across pharmacies) */}
          {Array.isArray(order.shipments) && order.shipments.length > 0 && (
            <Card>
              <CardHeader title="Shipments" subtitle="Tracking across all fulfilling pharmacies" />
              <div className="p-6 pt-0 space-y-3">
                {order.shipments.map((shipment: OrderShipment) => (
                  <div
                    key={shipment.id}
                    className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-accent-50 dark:bg-accent-900/30 rounded-lg">
                        <TruckIcon className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                          {shipment.carrier ? formatProviderName(shipment.carrier) : 'Carrier'}
                        </p>
                        {shipment.trackingNumber && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 font-mono">{shipment.trackingNumber}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={getShipmentStatusVariant(shipment.status)} size="sm">
                        {formatShipmentStatus(shipment.status)}
                      </Badge>
                      {shipment.trackingNumber && shipment.carrier && (
                        <a
                          href={getTrackingUrl(shipment.carrier, shipment.trackingNumber)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Track
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader title="Customer" />
            <div className="p-6 pt-0">
              {order.patient ? (
                <Link
                  to={`/patients/${order.patientId}`}
                  className="flex items-center gap-3 rounded-lg p-2 -m-2 transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-700/50 group"
                >
                  <Avatar
                    firstName={order.patient.firstName}
                    lastName={order.patient.lastName}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-secondary-900 dark:text-secondary-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {order.patient.firstName} {order.patient.lastName}
                    </p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">{order.patient.email}</p>
                  </div>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 text-secondary-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400">No customer information</p>
              )}
            </div>
          </Card>

          {/* Shipping Address */}
          <Card>
            <CardHeader
              title="Shipping"
              action={canUpdateShipping && (
                <Button size="sm" variant="ghost" onClick={() => setShippingModalOpen(true)}>
                  Edit
                </Button>
              )}
            />
            <div className="p-6 pt-0">
              {order.shipping ? (
                <div className="space-y-1 text-sm">
                  <p className="text-secondary-900 dark:text-secondary-100">{order.shipping.streetAddress1}</p>
                  {order.shipping.streetAddress2 && (
                    <p className="text-secondary-900 dark:text-secondary-100">{order.shipping.streetAddress2}</p>
                  )}
                  <p className="text-secondary-900 dark:text-secondary-100">
                    {order.shipping.city}, {order.shipping.state} {order.shipping.postalCode}
                  </p>
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-sm">No shipping address</p>
              )}
            </div>
          </Card>

          {/* Status Timeline */}
          <Card>
            <CardHeader title="Activity" />
            <div className="p-6 pt-0">
              {order.orderStatusUpdates && order.orderStatusUpdates.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-secondary-200 dark:bg-secondary-700" />

                  <div className="space-y-4">
                    {order.orderStatusUpdates.map((update: OrderStatusUpdate, index: number) => (
                      <div key={update.id} className="relative flex gap-3 pl-6">
                        {/* Timeline dot */}
                        <div className={`absolute left-0 w-4 h-4 rounded-full border-2 bg-white ${
                          index === 0 ? 'border-primary-500' : 'border-secondary-300'
                        }`}>
                          {index === 0 && (
                            <div className="absolute inset-1 rounded-full bg-primary-500" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={getRawStatusVariant(update.status)} size="sm">
                              {formatRawStatus(update.status)}
                            </Badge>
                            <span className="text-xs text-secondary-400 dark:text-secondary-500">
                              via {update.sourceType}
                            </span>
                          </div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                            {formatDateTime(update.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-sm">No activity yet</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelOrder}
        title="Cancel Order"
        message={`Are you sure you want to cancel order ${order.orderCode || order.id.slice(0, 8)}? This action cannot be undone.`}
        confirmLabel="Cancel Order"
        isLoading={isCancelling}
      />

      {/* Refund Confirmation Dialog */}
      <ConfirmDialog
        isOpen={refundDialogOpen}
        onClose={() => { setRefundDialogOpen(false); setSelectedTransaction(null); }}
        onConfirm={handleRefundTransaction}
        title="Refund Transaction"
        message={selectedTransaction ? `Refund ${formatCurrency(selectedTransaction.amount)} for transaction ${selectedTransaction.transactionId}?` : ''}
        confirmLabel="Refund"
        isLoading={isRefunding}
      />

      {/* Edit Shipping Modal */}
      <Modal isOpen={shippingModalOpen} onClose={() => setShippingModalOpen(false)} title="Edit Shipping Address">
        <div className="space-y-4">
          <Input
            label="Street Address"
            value={shippingForm.streetAddress1}
            onChange={(e) => setShippingForm({ ...shippingForm, streetAddress1: e.target.value })}
            required
          />
          <Input
            label="Street Address 2"
            value={shippingForm.streetAddress2 || ''}
            onChange={(e) => setShippingForm({ ...shippingForm, streetAddress2: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              value={shippingForm.city}
              onChange={(e) => setShippingForm({ ...shippingForm, city: e.target.value })}
              required
            />
            <Input
              label="State"
              value={shippingForm.state}
              onChange={(e) => setShippingForm({ ...shippingForm, state: e.target.value })}
              required
            />
          </div>
          <Input
            label="Postal Code"
            value={shippingForm.postalCode}
            onChange={(e) => setShippingForm({ ...shippingForm, postalCode: e.target.value })}
            required
          />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShippingModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveShipping} isLoading={isSavingShipping}>Save Changes</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderDetails;
